import React, { useMemo } from 'react'
import { CustomReport, PrintStyleSettings } from '../../../../../redux/context/customReports/types'
import { CustomReportSectionsType } from '../../../../../redux/context/customReports/typesSection'
import { getTitleByLanguage } from '../customReportTable/utils'
import { formatAsDate } from '../../../../../config/dayjsConfig'
import { PageBreak } from './pageBreakUtils'
import { buildGridTemplate } from './gridTemplate'
import { PaperSheet, PrintGlobalStyle } from './StyledPaper'
import PrintReportHeader from './PrintReportHeader'
import PrintReportContent from './PrintReportContent'
import PageBreakOverlay from './PageBreakOverlay'
import { WATERMARK } from './paperConfig'

interface PrintPaperProps {
  report: CustomReport
  evaluatedSectionMap: Map<number, CustomReportSectionsType | null>
  settings: PrintStyleSettings
  mode?: 'drawer' | 'print'
  contentRef: React.RefObject<HTMLDivElement>
  breakPositions?: PageBreak[]
  companyName?: string
  date?: string | null
  logoUrl?: string | null
  contractId?: string
  token?: string
  printableWidthPx: number
  forcedBreaks?: Set<number>
  onToggleForcedBreak?: (sectionId: number) => void
  counterZoom?: number
  style?: React.CSSProperties
  className?: string
}

const PrintPaper: React.FC<PrintPaperProps> = ({
  breakPositions = [],
  className,
  companyName,
  contentRef,
  contractId,
  counterZoom,
  date,
  evaluatedSectionMap,
  forcedBreaks,
  logoUrl,
  mode,
  printableWidthPx,
  report,
  settings,
  style,
  token,
  onToggleForcedBreak
}) => {
  const sections = report.sections || []

  const { areas, rows, sectionMap, heightMap, colSpanMap } = useMemo(
    () => buildGridTemplate(sections, report.layouts?.lg),
    [sections, report.layouts]
  )

  return (
    <>
      <PrintGlobalStyle
        $paperSize={settings.paperSize}
        $orientation={settings.orientation}
        $margin={settings.margin}
        $pageNumbers={settings.pageNumbers}
        $watermark={settings.watermark}
        $mode={mode}
      />
      <PaperSheet
        className={className ?? 'print-paper'}
        $paperSize={settings.paperSize}
        $orientation={settings.orientation}
        $scale={settings.scale}
        $margin={settings.margin}
        style={style}
      >
        <div ref={contentRef} style={{ position: 'relative' }}>
          <PrintReportHeader
            companyName={companyName}
            reportTitle={getTitleByLanguage(report)}
            date={formatAsDate(date)}
            logoUrl={logoUrl}
            contractId={contractId}
            token={token}
          />
          <PrintReportContent
            evaluatedSectionMap={evaluatedSectionMap}
            sectionMap={sectionMap}
            heightMap={heightMap}
            colSpanMap={colSpanMap}
            areas={areas}
            rows={rows}
            printableWidthPx={printableWidthPx}
            forcedBreaks={forcedBreaks}
            onToggleForcedBreak={onToggleForcedBreak}
            counterZoom={counterZoom}
          />
          {settings.pageBreaks && breakPositions.length > 0 && (
            <PageBreakOverlay breaks={breakPositions} margin={settings.margin} scale={settings.scale} />
          )}
        </div>
        {settings.watermark !== false && (
          <div
            className="print-watermark"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12
            }}
          >
            <span style={{ fontSize: WATERMARK.fontSize, fontFamily: WATERMARK.fontFamily, color: WATERMARK.color }}>
              {WATERMARK.text}
            </span>
          </div>
        )}
      </PaperSheet>
    </>
  )
}

export default PrintPaper
